import React from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  LinearProgress,
  Collapse,
  Alert,
  Chip,
  Fade,
  Stack,
  CircularProgress,
  Zoom,
  Slide
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import WarningIcon from '@mui/icons-material/Warning';

const NotificationCard = ({ notification, onClose, onNavigate }) => {
  const { id, type, title, message, progress, questionInfo, segmentId } = notification;
  
  const getStatusIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon sx={{ fontSize: 20 }} color="success" />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 20 }} color="error" />;
      case 'processing':
        return <CircularProgress size={20} thickness={4} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 20 }} color="warning" />;
      default:
        return null;
    }
  };

  const getBackgroundColor = (theme) => {
    switch (type) {
      case 'success':
        return theme.palette.success.light;
      case 'error':
        return theme.palette.error.light;
      case 'processing':
        return theme.palette.grey[100];
      case 'warning':
        return theme.palette.warning.light;
      default:
        return theme.palette.grey[100];
    }
  };

  const getBorderColor = (theme) => {
    switch (type) {
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'processing':
        return theme.palette.primary.main;
      case 'warning':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[400];
    }
  };

  return (
    <Card 
      elevation={4}
      sx={{ 
        mb: 1.5,
        backgroundColor: getBackgroundColor,
        borderLeft: 3,
        borderLeftColor: getBorderColor,
        position: 'relative',
        overflow: 'visible',
        minWidth: 200,
        maxWidth: 250
      }}
    >
      <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon()}
            <Typography variant="body2" fontWeight="medium">
              {type === 'processing' ? 'Locking' : title}
            </Typography>
            {questionInfo && (
              <Chip 
                size="small"
                label={`Q${questionInfo.number}${questionInfo.part ? questionInfo.part : ''}`}
                onClick={onNavigate && type !== 'processing' ? () => onNavigate(segmentId) : undefined}
                sx={{ 
                  cursor: onNavigate && type !== 'processing' ? 'pointer' : 'default',
                  height: 18,
                  fontSize: '0.7rem',
                  '& .MuiChip-label': {
                    px: 0.75
                  },
                  '&:hover': onNavigate && type !== 'processing' ? {
                    backgroundColor: 'action.hover'
                  } : {}
                }}
              />
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => onClose(id)}
            sx={{ 
              p: 0.25,
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

const NotificationSystem = ({ notifications, onClose, onNavigateToQuestion, position = 'top-right' }) => {
  const getPositionStyles = () => {
    const baseStyles = {
      position: 'fixed',
      zIndex: 1400,
      maxWidth: 400,
      width: '100%',
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto'
      }
    };

    switch (position) {
      case 'top-right':
        return { ...baseStyles, top: 80, right: 20 }; // Moved down to be below header
      case 'top-left':
        return { ...baseStyles, top: 80, left: 20 };
      case 'bottom-right':
        return { ...baseStyles, bottom: 20, right: 20 };
      case 'bottom-left':
        return { ...baseStyles, bottom: 20, left: 20 };
      default:
        return { ...baseStyles, top: 80, right: 20 };
    }
  };

  return (
    <Box sx={getPositionStyles()}>
      <Stack spacing={1}>
        {notifications.map((notification, index) => (
          <Zoom 
            key={notification.id} 
            in={true}
            timeout={300}
            style={{
              transitionDelay: `${index * 50}ms`
            }}
          >
            <Box>
              <NotificationCard
                notification={notification}
                onClose={onClose}
                onNavigate={onNavigateToQuestion}
              />
            </Box>
          </Zoom>
        ))}
      </Stack>
    </Box>
  );
};

export default NotificationSystem;